import { Ref } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { Star } from '@phosphor-icons/react';
import { format, Locale } from 'date-fns';
import { ActivityInstance } from '@api';
import { getChildActivityStatus } from '@/components/child';
import WeekActivityRow from './WeekActivityRow';
import type { WeekTokens } from './weekTokens';

type WeekDayCardProps = {
  /** Ref till kortets rot (sätts endast på dagens kort → scroll-till-idag). */
  rootRef?: Ref<HTMLDivElement>;
  day: Date;
  instances: ActivityInstance[];
  tokens: WeekTokens;
  locale: Locale;
  isToday: boolean;
  isPast: boolean;
  /** Aktuell tidpunkt (för nu-markören i dagens kort). */
  now: Date;
  /** "Nu"-etikett för nu-markören. */
  nowLabel: string;
  /** Hopfällbar dag (teen). false = alltid öppen (barn/adult). */
  collapsible: boolean;
  /** Endast relevant när collapsible: är kortet expanderat? */
  expanded: boolean;
  onToggle: () => void;
  /** Klar-räknare {dayDone}/{dayTotal} i headern (adult + teen). Frikopplad från collapsible. */
  showDayCount: boolean;
  /** Teen: status-dots på hopfällt kort. */
  showStatusDots: boolean;
  /** Teen: guldstjärna på 100%-klara dagar. */
  showDayStar: boolean;
  /** Barn: emoji-ikon i aktivitetsraderna. */
  showEmoji: boolean;
  /** Teen: avgör om en aktivitets stjärnvärde väntar på besked. Utelämnad = ingen markering. */
  isStarValuePending?: (instance: ActivityInstance) => boolean;
  /** Färdigöversatt etikett för väntan-markeringen. */
  starValuePendingLabel?: string;
  /** Teen: pulserande glow runt "idag"-kortet. */
  todayGlow: boolean;
  todayLabel: string;
  noActivitiesLabel: string;
  onActivityClick: (instanceId: number) => void;
};

/**
 * WeekDayCard — EN dag = ETT kort, gemensamt för alla tre personas.
 *
 * Konsistens-kontraktet:
 *  - Dagshuvud: dagnamn + datum i samma ordning/plats överallt.
 *  - "Idag" markeras med SAMMA mekanism överallt: accent-ram + "Idag"-etikett.
 *  - Passerade dagar behandlas lika (dimmas när de är hopfällda/stängda).
 *
 * Additiva lager (bevarad lekfullhet, styrs av flaggor):
 *  - collapsible: teens hopfällbara accordion (barn/adult: alltid öppna).
 *  - showStatusDots / showDayStar: teens scanning-hjälp + gamification.
 *  - todayGlow: teens glow runt dagens kort.
 */
const WeekDayCard = ({
  rootRef,
  day,
  instances,
  tokens,
  locale,
  isToday,
  isPast,
  now,
  nowLabel,
  collapsible,
  expanded,
  onToggle,
  showDayCount,
  showStatusDots,
  showDayStar,
  showEmoji,
  isStarValuePending,
  starValuePendingLabel,
  todayGlow,
  todayLabel,
  noActivitiesLabel,
  onActivityClick,
}: WeekDayCardProps) => {
  // Alltid-öppna dagar (barn/adult) betraktas som permanent expanderade.
  const isOpen = collapsible ? expanded : true;
  const dayDone = instances.filter((i) => getChildActivityStatus(i) === 'done').length;
  const dayTotal = instances.length;
  const isComplete = dayTotal > 0 && dayDone === dayTotal;

  const glowActive = todayGlow && isToday;

  // Nu-markörens position i dagens lista: före första aktivitet som ännu inte
  // startat. Alla passerade → sist (=== length). Endast relevant för dagens kort.
  const firstUpcoming = instances.findIndex(
    (inst) => new Date(inst.startDate).getTime() > now.getTime(),
  );
  const nowIndex = isToday ? (firstUpcoming === -1 ? instances.length : firstUpcoming) : -1;

  const nowMarker = (
    <Box aria-hidden sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 0.5, py: 0.25 }}>
      <Box
        sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: tokens.accent, flexShrink: 0 }}
      />
      <Box sx={{ flex: 1, height: '2px', bgcolor: tokens.accent, opacity: 0.5, borderRadius: 1 }} />
      <Typography
        sx={{
          fontSize: '0.65rem',
          fontWeight: 700,
          color: tokens.accent,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        {nowLabel} {format(now, 'HH:mm')}
      </Typography>
    </Box>
  );

  const header = (
    <>
      {/* Dagnamn + datum */}
      <Box sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: '0.95rem',
              textTransform: 'capitalize',
              color: isToday ? tokens.accent : tokens.text,
            }}
          >
            {format(day, 'EEEE', { locale })}
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', color: tokens.textSoft, fontWeight: 600 }}>
            {format(day, 'd MMM', { locale })}
          </Typography>
          {isToday && (
            <Box
              sx={{
                bgcolor: tokens.accent,
                color: tokens.cardBg,
                fontSize: '0.6rem',
                fontWeight: 700,
                px: 0.75,
                py: 0.125,
                borderRadius: '4px',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              {todayLabel}
            </Box>
          )}
        </Box>
      </Box>

      {/* Status-dots (teen, endast hopfällt kort) */}
      {showStatusDots && collapsible && !isOpen && dayTotal > 0 && (
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          {instances.slice(0, 5).map((inst) => {
            const done = getChildActivityStatus(inst) === 'done';
            return (
              <Box
                key={inst.activityInstanceId}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: done ? tokens.done : tokens.textSoft,
                }}
              />
            );
          })}
          {instances.length > 5 && (
            <Typography sx={{ fontSize: '0.6rem', color: tokens.textSoft, fontWeight: 600 }}>
              +{instances.length - 5}
            </Typography>
          )}
        </Box>
      )}

      {/* Räknare + completion-stjärna. Räknaren är frikopplad från collapsible
          så att adult (alltid öppna dagar) får en aggregerad completion-signal. */}
      {showDayCount && dayTotal > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {showDayStar && isComplete && <Star size={14} weight="fill" color={tokens.done} />}
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: tokens.textSoft }}>
            {dayDone}/{dayTotal}
          </Typography>
        </Box>
      )}

      {collapsible && dayTotal === 0 && (
        <Typography sx={{ fontSize: '0.75rem', color: tokens.textSoft, fontWeight: 500 }}>
          {noActivitiesLabel}
        </Typography>
      )}
    </>
  );

  return (
    <Box
      ref={rootRef}
      sx={{
        // En passerad dag sjunker in i sidan (sidans bakgrund, ingen skugga) i
        // stället för att tonas med opacitet — 65 % opacitet drog datum och
        // "Ledigt" ner till 2,9:1. Texten behåller sin fulla kontrast.
        bgcolor: isPast && !isOpen ? tokens.pageBg : tokens.cardBg,
        border: isToday ? '1.5px solid' : '1px solid',
        borderColor: isToday ? tokens.accent : tokens.cardBorder,
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: glowActive || (isPast && !isOpen) ? undefined : tokens.cardShadow,
        animation: glowActive ? 'teenWeekGlow 2.4s ease-in-out infinite' : undefined,
        transition: 'background-color 0.15s, box-shadow 0.2s',
        '@media (prefers-reduced-motion: reduce)': { transition: 'none', animation: 'none' },
      }}
    >
      {collapsible ? (
        <Box
          component="button"
          type="button"
          onClick={onToggle}
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            p: '14px 16px',
            border: 'none',
            bgcolor: 'transparent',
            cursor: 'pointer',
            fontFamily: 'inherit',
            textAlign: 'left',
            gap: 1.5,
            '&:active': { opacity: 0.8 },
          }}
        >
          {header}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', p: '14px 16px', gap: 1.5 }}>{header}</Box>
      )}

      {/* Aktivitetslista */}
      {isOpen && (
        <Box
          sx={{
            px: 1.5,
            pb: 1.5,
            borderTop: collapsible ? `1px solid ${tokens.cardBorder}` : 'none',
            pt: collapsible ? 1 : 0,
          }}
        >
          {dayTotal === 0 ? (
            <Typography
              sx={{ fontSize: '0.85rem', color: tokens.textSoft, fontWeight: 600, px: 1 }}
            >
              {noActivitiesLabel}
            </Typography>
          ) : (
            <Stack spacing={0.5}>
              {instances.map((inst: ActivityInstance, i: number) => (
                <Box key={inst.activityInstanceId}>
                  {isToday && i === nowIndex && nowMarker}
                  <WeekActivityRow
                    instance={inst}
                    tokens={tokens}
                    showEmoji={showEmoji}
                    starValuePending={isStarValuePending?.(inst) ?? false}
                    starValuePendingLabel={starValuePendingLabel}
                    onClick={() => onActivityClick(inst.activityInstanceId)}
                  />
                </Box>
              ))}
              {/* Alla aktiviteter passerade → nu-markören ligger sist. */}
              {isToday && nowIndex === instances.length && nowMarker}
            </Stack>
          )}
        </Box>
      )}
    </Box>
  );
};

export default WeekDayCard;
